import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import PayPalButton from '../components/PayPalButton'

const PaymentPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  
  // Get order data from navigation state (passed from checkout)
  const orderData = location.state?.orderData

  if (!orderData) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Bestellung nicht gefunden</h1>
            <p className="text-gray-600 mb-6">
              Es scheint, als wäre ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
            </p>
            <button 
              onClick={() => navigate('/checkout')}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Zurück zur Kasse
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/checkout')}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-6"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Zurück zur Kasse
          </button>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
              <h1 className="text-2xl font-bold mb-2">Zahlung abschließen</h1>
              <p className="opacity-90">
                Bestellnummer: <span className="font-semibold">{orderData.orderNumber}</span>
              </p>
            </div>

            {/* Order Summary */}
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold mb-4">Bestellübersicht</h2>
              
              <div className="space-y-3 mb-4">
                {orderData.items.map((item: any, index: number) => (
                  <div key={index} className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium">{item.title}</h3>
                      <p className="text-sm text-gray-600">
                        {item.device.brand} {item.device.deviceModel}
                        {item.isExpress && <span className="text-orange-600 ml-2">Express</span>}
                      </p>
                      <p className="text-sm text-gray-500">Menge: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-indigo-600">{item.price.toFixed(2)}€</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center text-xl font-bold">
                  <span>Gesamtbetrag:</span>
                  <span className="text-indigo-600">{orderData.total.toFixed(2)}€</span>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold mb-4">Kundendaten</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium">{orderData.customerInfo.name}</p>
                  <p className="text-gray-600">{orderData.customerInfo.email}</p>
                  <p className="text-gray-600">{orderData.customerInfo.phone}</p>
                </div>
                <div>
                  <p className="text-gray-600">{orderData.customerInfo.address}</p>
                  <p className="text-gray-600">
                    {orderData.customerInfo.zipCode} {orderData.customerInfo.city}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Section */}
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4">Zahlungsmethode</h2>
              
              <div className="mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-2">
                    <div className="text-blue-600 font-semibold">PayPal</div>
                    <div className="ml-auto text-sm text-blue-600">Sicher & Schnell</div>
                  </div>
                  <p className="text-sm text-blue-700">
                    Bezahlen Sie sicher mit PayPal oder Kreditkarte
                  </p>
                </div>
                
                <PayPalButton orderData={orderData} />
              </div>

              <div className="text-center text-xs text-gray-500 mt-4">
                <p>🔒 Ihre Daten werden verschlüsselt übertragen</p>
                <p>Mit dem Abschluss der Zahlung akzeptieren Sie unsere AGB</p>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
            <h3 className="font-semibold mb-3">Was passiert als Nächstes?</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• Nach erfolgreicher Zahlung erhalten Sie eine Bestätigungs-E-Mail</p>
              <p>• Das kostenlose DHL-Versandlabel wird Ihnen zugesendet</p>
              <p>• Verpacken Sie Ihr Gerät sicher und senden es an uns</p>
              <p>• Wir reparieren Ihr Gerät und senden es zurück</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentPage